import { Subtitle, Description } from '@snapi/luxury';
import { useToggle } from 'react-use';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Variants } from 'framer-motion';
import clsx from 'clsx';
import { SunIcon } from '@snapi/icons';

const CardVariants: Variants = {
  open: {
    height: 'auto',
    opacity: 1,
    pointerEvents: 'auto',
  },
  closed: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
  },
};

const Arrow = () => {
  return (
    <svg
      width="13"
      height="8"
      viewBox="0 0 13 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.3536 4.35355C12.5488 4.15829 12.5488 3.84171 12.3536 3.64645L9.17157 0.464466C8.97631 0.269204 8.65973 0.269204 8.46447 0.464466C8.2692 0.659728 8.2692 0.976311 8.46447 1.17157L11.2929 4L8.46447 6.82843C8.2692 7.02369 8.2692 7.34027 8.46447 7.53553C8.65973 7.7308 8.97631 7.7308 9.17157 7.53553L12.3536 4.35355ZM0 4.5H12V3.5H0V4.5Z"
        fill="#595959"
      />
    </svg>
  );
};

const ArrowUp = () => {
  return (
    <svg
      width="8"
      height="13"
      viewBox="0 0 8 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M4.35355 0.646446C4.15829 0.451184 3.84171 0.451184 3.64645 0.646446L0.464466 3.82843C0.269204 4.02369 0.269204 4.34027 0.464466 4.53553C0.659728 4.7308 0.976311 4.7308 1.17157 4.53553L4 1.70711L6.82843 4.53553C7.02369 4.7308 7.34027 4.7308 7.53553 4.53553C7.7308 4.34027 7.7308 4.02369 7.53553 3.82843L4.35355 0.646446ZM4.5 13L4.5 1H3.5L3.5 13H4.5Z"
        fill="#595959"
      />
    </svg>
  );
};

const allHealthRec = [
  {
    title: 'Provide opportunities for outdoor play',
    description:
      'Exposure to natural environments and outdoor play has been associated with a more diverse gut microbiome.',
    research: 'https://www.pnas.org/doi/10.1073/pnas.1313731110',
  },
  {
    title: 'Encourage physical activity',
    description:
      'Regular physical activity has been linked to a healthier gut microbiome in children.',
    research: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10080385/',
  },
  {
    title: 'Limit screen time',
    description:
      'Excessive screen time has been associated with changes in gut bacteria and other negative health outcomes.',
    research: 'https://pubmed.ncbi.nlm.nih.gov/35236903/',
  },
];
const nextHealthRec = [
  {
    title: 'Encourage good sleep habits',
    description:
      'Adequate sleep is important for overall health, including a healthy gut microbiome.',
    research: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC6779243/',
  },
  {
    title: 'Promote stress management',
    description:
      'Stress can have negative effects on the gut microbiome, so it`s important to help children develop healthy ways of coping with stress, such as through play or mindfulness techniques.',
    research: 'https://pubmed.ncbi.nlm.nih.gov/21282636/',
  },
  {
    title: 'Exposure to furry pets',
    description:
      'Studies have shown that children who grow up in households with pets tend to have a more diverse microbiome, which may help to protect against certain health conditions such as allergies and asthma.',
    research:
      'https://www.frontiersin.org/articles/10.3389/fcimb.2020.00073/full',
  },
];
interface OneHealthCardProps {
  title: string;
  description: string;
  research: string;
}

const OneHealthCard = ({
  title,
  description,
  research,
}: OneHealthCardProps) => {
  const [on, togle] = useToggle(false);
  return (
    <div className="border-[1px] border-gray-soft rounded-[10px]">
      <div
        onClick={togle}
        className={clsx(
          'flex justify-between items-center p-3 bg-white rounded-[10px]',
          on && 'rounded-b-none'
        )}
      >
        <div className="flex gap-3 justify-center items-center">
          <SunIcon className="text-yellow" />
          <div className="pb-4">
            {' '}
            <Subtitle>{title}</Subtitle>
          </div>
        </div>
        {on ? (
          <div>
            <ArrowUp />{' '}
          </div>
        ) : (
          <div>
            <Arrow />{' '}
          </div>
        )}
      </div>
      <motion.div
        layout
        animate={on ? 'open' : 'closed'}
        variants={CardVariants}
        transition={{ duration: 0.6 }}
      >
        <div
          className={clsx(
            'flex flex-col p-3 border-t-[1px] border-gray-soft bg-white rounded-[10px]',
            on && 'rounded-t-none'
          )}
        >
          <div className="mb-3">
            <Description>{description}</Description>
          </div>
          <div className="flex gap-3 items-center">
            <Subtitle>Research:</Subtitle>
            <Link href={research}>
              <p className="text-[12px] font-medium underline">Link 1</p>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const ReportHealthRecomendationsPdf = (props: { babyName: string }) => {
  return (
    <section>
      <div className="container">
        <div className="mb-10">
          <div className="">
            <h2 className="mb-3 text-dark">
              {props.babyName}’s tips for general health
            </h2>
          </div>
          <p className="">
            It is important to remember that your baby's health does not depend
            only on the content of any particular bacteria. In many ways, it
            also depends on the diversity of bacteria and external factors. We
            have put together some general recommendations for you that we think
            are important for {props.babyName}’s overall health.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-2">
            {allHealthRec.map((rec) => {
              return <OneHealthCard {...rec} />;
            })}
          </div>
          <div className="flex flex-col gap-2">
            {nextHealthRec.map((rec) => {
              return <OneHealthCard {...rec} />;
            })}{' '}
          </div>
        </div>
      </div>
    </section>
  );
};
